/*
 * /debt-indexation — indexation of court-awarded sums (Russia: ст. 208 ГПК,
 * ст. 183 АПК, ст. 189.1 КАС). Math: legal-ru.js on core.js data; plumbing:
 * ui.js. What a lawyer needs from it, in the order they need it:
 *   1. the number, to the kopeck, by the Supreme Court's method;
 *   2. the calculation table a court will check line by line;
 *   3. the application with the numbers already in — a Word file, because an
 *      application is edited before it is filed (statement-ru.js);
 *   4. honesty about what the number does NOT cover — an unpublished month,
 *      a part paid more than a year ago, deflation outweighing inflation,
 *      and money that has not been paid yet (a court indexes only what has).
 * Copying the calculation is the action people take most on our legal
 * calculators (penalty-calculator telemetry: copy 90, csv 10, print 7 in 90
 * days), so «copy» is the primary button.
 */
import * as C from './core.js';
import * as L from './legal-ru.js';
import * as ST from './statement-ru.js';
import * as U from './ui.js';

const SLUG = 'debt-indexation';
const { $, $$, tr, esc } = U;

const today = (() => { const t = new Date(); return { y: t.getFullYear(), m: t.getMonth() + 1, d: t.getDate() }; })();
const S = {
    // The calculation is one for every court; the code only words the
    // application (which article, which requisites). `lawSet` — a person, a
    // link or the court's own name has named it; until then nothing we print
    // outside the application names an article.
    law: 'gpk', lawSet: false, budget: false, method: 'parts', floorZero: true,
    amount: NaN, start: '', end: L.dateStr(today),
    // Is the debt still owed? Until the person says when the money came, it
    // is (the end date is today's by default); once they pick a date, that is
    // the day it came. `unpaidAuto` — nobody has touched the switch itself.
    unpaid: true, unpaidAuto: true,
    payments: [],             // [{date:'YYYY-MM-DD', amount:Number}]
    court: '', caseNo: '', creditor: '', debtor: '',
    i395: { from: '', mor: true },
};
let statement = null;         // the application for the result on screen, or why there is none
// The Bank of Russia key rate as the page printed it (partials/key-rate-live).
const KEY_RATE = Array.isArray(window.__KEY_RATE_LIVE) && window.__KEY_RATE_LIVE.length > 60 ? window.__KEY_RATE_LIVE : null;
let last395 = null;
let ds = null;
let last = null;
const T = U.tracker();

async function boot() {
    readHash();
    try {
        ds = await U.dataset(U.CFG.country);
    } catch (e) {
        $('#di-status').textContent = tr('load_failed');
        U.diagnostic('dataset_failed', { iso: U.CFG.country, msg: String(e && e.message || e).slice(0, 120) });
        window.__handleError && window.__handleError(e, 'indexation:dataset');
        return;
    }
    $('#di-status').textContent = tr('status', { source: tr('src_' + (ds.meta.source || 'rosstat')), last: U.monthYear(ds.k1), checked: ds.fetchedAt ? U.longDate(dateOf(ds.fetchedAt)) : '' });
    paintInputs();
    wire();
    render();
    U.stage('ready');
}
const dateOf = iso => ({ y: +iso.slice(0, 4), m: +iso.slice(5, 7), d: +iso.slice(8, 10) });

function readHash() {
    const h = U.readHash();
    // A shared link reopens a calculation: worth knowing how often that happens.
    if (h.a || h.s) U.funnel('from_link', { pays: h.p ? h.p.split('|').length : 0 });
    if (['gpk', 'apk', 'kas'].includes(h.law)) { S.law = h.law; S.lawSet = true; }
    if (h.b === '1') S.budget = true;
    if (h.mt === 'balance') S.method = 'balance';
    const a = U.parseAmount(h.a);
    if (Number.isFinite(a) && a > 0) S.amount = a;
    if (L.parseDate(h.s)) S.start = h.s;
    if (L.parseDate(h.e)) S.end = h.e;
    // u=1 — still owed, u=0 — paid on the end date. A link made before the
    // switch existed has neither: an end date in the past was the day the
    // money came, today's (or none) — a debt counted «as of today».
    if (h.u === '1' || h.u === '0') { S.unpaid = h.u === '1'; S.unpaidAuto = false; }
    else if (L.parseDate(h.e) && h.e < L.dateStr(today)) { S.unpaid = false; S.unpaidAuto = false; }
    if (L.parseDate(h.pf)) S.i395.from = h.pf;
    if (h.pm === '0') S.i395.mor = false;
    if (h.p) {
        S.payments = h.p.split('|').map(x => { const [d, v] = x.split('~'); return { date: d, amount: U.parseAmount(v) }; }).filter(p => L.parseDate(p.date) && p.amount > 0);
    }
}

function paintInputs() {
    $('#di-amount').value = Number.isFinite(S.amount) ? U.num(S.amount, S.amount % 1 ? 2 : 0) : '';
    $('#di-start').value = S.start;
    $('#di-end').value = S.end;
    $('#di-end').max = L.dateStr(today);
    $('#di-unpaid').checked = S.unpaid;
    $('#di-budget').checked = S.budget;
    $('#di-395').hidden = !KEY_RATE;
    $('#di-395-from').value = S.i395.from;
    $('#di-395-mor').checked = S.i395.mor;
    if (S.i395.from || !S.i395.mor) $('#di-395').open = true;
    showLaw();
    $$('[data-method]').forEach(b => b.classList.toggle('on', b.dataset.method === S.method));
    paintPayments();
    labelStart();
    labelEnd();
}

function showLaw() {
    $$('[data-law]').forEach(b => b.classList.toggle('on', b.dataset.law === S.law));
    $$('[data-law-hint]').forEach(p => { p.hidden = p.dataset.lawHint !== S.law; });
}

function labelStart() {
    $('#di-start-l').textContent = tr(S.budget ? 'f_start_budget' : 'f_start');
    $('#di-start-hint').textContent = tr(S.budget ? 'f_start_budget_hint' : 'f_start_hint');
}

/** The end date is the day the money came — or, for a debt still owed, the day to count to: the hint says which. */
function labelEnd() {
    $('#di-end-hint').textContent = tr(S.unpaid ? 'f_end_unpaid_hint' : 'f_end_hint');
}

function paintPayments() {
    const box = $('#di-pays');
    box.innerHTML = S.payments.map((p, i) => `<div class="di-pay" data-i="${i}">
        <input class="tc-in" type="date" data-pd value="${esc(p.date)}" aria-label="${esc(tr('pay_date'))}">
        <input class="tc-in" data-pa inputmode="decimal" autocomplete="off" value="${Number.isFinite(p.amount) ? esc(U.num(p.amount, p.amount % 1 ? 2 : 0)) : ''}" aria-label="${esc(tr('pay_amount'))}" placeholder="${esc(tr('pay_amount'))}">
        <button type="button" class="tc-btn sm ghost di-x" data-rm aria-label="${esc(tr('pay_remove'))}">✕</button></div>`).join('');
    $('#di-method-wrap').hidden = S.payments.length === 0;
    // Sums of payments group their digits as they are typed, like the debt itself.
    $$('[data-pa]', box).forEach(el => U.bindAmount(el, v => {
        const p = S.payments[+el.closest('.di-pay').dataset.i];
        if (!p) return;
        p.amount = v;
        T.touch('pay_amount');
        render();
    }));
}

function wire() {
    U.bindAmount($('#di-amount'), v => { S.amount = v; T.touch('amount'); render(); });
    // A date typed into «the day the money came» says the money came: the
    // «not paid yet» mark steps aside unless the person set it themselves.
    const picked = key => {
        if (key !== 'end' || !S.unpaidAuto || !S.unpaid) return;
        S.unpaid = false;
        S.unpaidAuto = false;
        $('#di-unpaid').checked = false;
        labelEnd();
    };
    for (const [id, key] of [['#di-start', 'start'], ['#di-end', 'end']]) {
        $(id).addEventListener('change', e => { S[key] = e.target.value; if (L.parseDate(S[key])) picked(key); T.touch(key); render(); });
        $(id).addEventListener('input', e => { if (L.parseDate(e.target.value)) { S[key] = e.target.value; picked(key); T.touch(key); render(); } });
    }
    $('#di-unpaid').addEventListener('change', e => {
        S.unpaid = e.target.checked;
        S.unpaidAuto = false;
        // «Not paid yet» counts to today.
        if (S.unpaid) { S.end = L.dateStr(today); $('#di-end').value = S.end; }
        labelEnd();
        T.touch('unpaid');
        render();
        U.funnel('setting', { k: 'unpaid', v: S.unpaid ? 1 : 0 });
    });
    $('#di-budget').addEventListener('change', e => { S.budget = e.target.checked; T.touch('budget'); labelStart(); render(); U.funnel('setting', { k: 'budget', v: S.budget ? 1 : 0 }); });
    $$('[data-law]').forEach(b => b.addEventListener('click', () => { S.law = b.dataset.law; S.lawSet = true; showLaw(); T.touch('law'); render(); U.funnel('law', { law: S.law }); }));
    $$('[data-method]').forEach(b => b.addEventListener('click', () => { S.method = b.dataset.method; $$('[data-method]').forEach(x => x.classList.toggle('on', x === b)); T.touch('method'); render(); U.funnel('method', { m: S.method }); }));
    $('#di-add').addEventListener('click', () => {
        S.payments.push({ date: S.end || L.dateStr(today), amount: NaN });
        paintPayments();
        const rows = $$('.di-pay');
        rows.length && $('[data-pa]', rows[rows.length - 1]).focus();
        T.touch('add_pay');
        U.funnel('add_pay', { n: S.payments.length });
    });
    $('#di-pays').addEventListener('input', e => {
        if (!e.target.matches('[data-pd]')) return;      // sums: bindAmount in paintPayments
        const row = e.target.closest('.di-pay');
        if (!row) return;
        const p = S.payments[+row.dataset.i];
        p.date = e.target.value;
        T.touch('pay_date');
        render();
    });
    $('#di-pays').addEventListener('click', e => {
        const b = e.target.closest('[data-rm]');
        if (!b) return;
        S.payments.splice(+b.closest('.di-pay').dataset.i, 1);
        paintPayments();
        T.touch('pay_remove');
        render();
    });
    $('#di-paste-go').addEventListener('click', () => {
        const got = parsePaymentList($('#di-paste').value);
        if (!got.length) { window.__toast && window.__toast(tr('paste_none'), 'warning'); U.funnel('paste', { n: 0 }); return; }
        S.payments = S.payments.filter(p => L.parseDate(p.date) && p.amount > 0).concat(got);
        $('#di-paste').value = '';
        $('#di-paste-box').open = false;
        paintPayments();
        T.touch('paste');
        render();
        window.__toast && window.__toast(tr('paste_ok', { n: got.length }), 'success');
        U.funnel('paste', { n: got.length });
    });
    for (const id of ['court', 'caseNo', 'creditor', 'debtor']) {
        $('#di-' + id).addEventListener('input', e => {
            S[id] = e.target.value;
            T.touch('details');
            // «Арбитражный суд города Москвы» says which code the application is under;
            // the link takes it too, so the whole result is drawn again.
            if (id === 'court' && !S.lawSet && S.law !== 'apk' && /арбитраж/i.test(S.court)) {
                S.law = 'apk';
                showLaw();
                U.funnel('law', { law: 'apk', by: 'court' });
                render();
                return;
            }
            renderStatement();
        });
    }
    $('#di-floor').addEventListener('change', e => { S.floorZero = e.target.checked; T.touch('floor'); render(); });
    $('#di-395-from').addEventListener('change', e => { S.i395.from = e.target.value; T.touch('i395_from'); render(); });
    $('#di-395-mor').addEventListener('change', e => { S.i395.mor = e.target.checked; T.touch('i395_mor'); render(); });
    $('#di-395').addEventListener('toggle', () => { if ($('#di-395').open) T.touch('i395'); render(); });
    $('#di-copy').addEventListener('click', () => { if (last) { U.copyText(calcText()); T.action('copy', { law: S.law }); } });
    const filled = () => ['court', 'caseNo', 'creditor', 'debtor'].filter(k => S[k].trim()).length;
    $('#di-copy-st').addEventListener('click', () => {
        if (!statement || !statement.ok) return;
        U.copyText(ST.toPlain(statement.blocks.concat(statement.appendix)));
        T.action('copy_statement', { law: S.law, filled: filled() });
    });
    $('#di-docx').addEventListener('click', downloadStatement);
    $('#di-link').addEventListener('click', () => { U.shareLink(location.href, document.title); T.action('link', {}); });
    $('#di-csv').addEventListener('click', () => {
        if (!last) return;
        const head = [tr('col_period'), tr('col_days'), tr('col_cpi'), tr('col_coef'), tr('col_cum')].join(';');
        const rows = last.months.map(m => [L.dateStr(m.from) + '..' + L.dateStr(m.to), m.days + '/' + m.dim, m.idx.toFixed(2), m.coef.toFixed(6), m.cum.toFixed(6)].join(';'));
        const blob = new Blob(['\uFEFF' + head + '\n' + rows.join('\n')], { type: 'text/csv;charset=utf-8' });
        window.__downloadFile && window.__downloadFile(blob, 'indexation-' + last.start + '-' + last.end + '.csv');
        T.action('csv', {});
    });
    $('#di-pdf').addEventListener('click', async () => {
        if (!window.__generatePDF || !last) return;
        const r = await window.__generatePDF({ element: $('#di-report'), filename: 'indexation-' + last.start + '-' + last.end + '.pdf', title: tr('pdf_title') });
        if (r && r.ok) T.action('pdf', {});
    });
    // On a phone the answer sits below the whole form; the line under the
    // fields shows it as it changes and brings the full answer into view.
    $('#di-live').addEventListener('click', () => {
        const a = $('#di-report .tc-panel');
        if (a) a.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        U.funnel('live_jump', {});
    });
    const drawer = el => el && el.closest('details');
    T.drawers({
        details: drawer($('#di-court')), pieces: drawer($('#di-pieces')), months: drawer($('#di-months')),
        statement: drawer($('#di-statement')), i395: $('#di-395'), rules: $('.di-rules') && $('.di-rules').closest('details'), paste: $('#di-paste-box'),
    });
}

/**
 * Payments pasted from a bank statement or a table: one per line, a date and
 * a sum in any order — «10.02.2024 70 000», «2024-02-10;70000,00»,
 * «70 000 руб. 10.02.2024». A line without both is skipped.
 */
export function parsePaymentList(text) {
    const out = [];
    for (const line of String(text || '').split(/\r?\n/)) {
        const dm = /(\d{1,2})[./-](\d{1,2})[./-](\d{4})|(\d{4})-(\d{2})-(\d{2})/.exec(line);
        if (!dm) continue;
        const date = dm[4] ? `${dm[4]}-${dm[5]}-${dm[6]}` : `${dm[3]}-${dm[2].padStart(2, '0')}-${dm[1].padStart(2, '0')}`;
        if (!L.parseDate(date)) continue;
        const rest = line.replace(dm[0], ' ');
        const am = /(\d[\d\s ]*(?:[.,]\d{1,2})?)/.exec(rest.trim());
        const amount = am ? U.parseAmount(am[1]) : NaN;
        if (amount > 0) out.push({ date, amount });
    }
    return out;
}

function render() {
    if (!ds) return;
    const o = {
        amount: S.amount, start: S.start, end: S.end, settled: !S.unpaid,
        payments: S.payments.filter(p => L.parseDate(p.date) && p.amount > 0),
        method: S.method, floorZero: S.floorZero, today: L.dateStr(today),
    };
    // A debt counted «as of today» (nobody named a date) carries no end date in
    // its link: reopened next week, it counts to that day.
    const asOfToday = S.unpaid && S.unpaidAuto;
    const state = {
        law: (S.lawSet || S.law !== 'gpk') ? S.law : '', b: S.budget ? 1 : '', mt: S.method !== 'parts' ? S.method : '',
        a: Number.isFinite(S.amount) ? S.amount : '', s: S.start, e: asOfToday ? '' : S.end,
        u: asOfToday ? '' : (S.unpaid ? 1 : 0),
        p: o.payments.map(p => p.date + '~' + p.amount).join('|'),
        pf: S.i395.from, pm: S.i395.mor ? '' : '0',
    };
    U.writeHash(state);
    const r = L.indexation(ds, o);
    if (!r.ok) {
        last = null;
        statement = null;
        // Each refusal names its own reason: one without a branch here used to
        // fall through to «err_order» and blamed the dates for something else.
        const key = r.errors.includes('amount') ? 'err_amount' : r.errors.includes('start') ? 'err_start' : r.errors.includes('end') ? 'err_end'
            : r.errors.includes('before_data') ? 'err_start_data' : r.errors.includes('after_data') ? 'err_start_unpublished' : 'err_order';
        const k0 = C.firstMonthIndex(ds);
        setAnswer('—', tr(key, { first: U.longDate({ y: C.ky(k0), m: C.km(k0), d: 1 }), last: U.monthYear(ds.k1) }), true);
        T.invalid(key);
        $('#di-stats').innerHTML = '';
        $('#di-msgs').innerHTML = '';
        $('#di-result-more').hidden = true;
        return;
    }
    last = r;
    const cur = ds.currency;
    const m = r.money;          // what is printed: kopecks, each part rounded on its own
    const numDay = d => U.numDate(L.parseDate(d));
    const totalI = r.months.length ? r.months[r.months.length - 1].cum : 1;
    setAnswer(U.money(m.total, cur, true), tr('ans_sub', { amount: U.money(r.amount, cur, true), from: numDay(r.start), to: numDay(r.end) }));
    const stats = [
        { l: tr('st_index'), v: U.signedPct((totalI - 1) * 100, 2), s: tr('st_index_s', { n: r.months.length }) },
        { l: tr('st_with'), v: U.money(r.unpaid + m.total, cur, true), s: r.paidInFull ? tr('st_with_paid') : tr('st_with_s') },
    ];
    if (r.paid > 0) stats.push({ l: tr('st_paid'), v: U.money(r.paid, cur, true), s: r.paidInFull ? tr('st_paid_full') : tr('st_left', { sum: U.money(r.unpaid, cur, true) }) });
    // Part paid, part owed: what a court will index today is its own figure.
    if (r.executed && !r.paidInFull) stats.push({ l: tr('st_claim'), v: U.money(m.claimNow, cur, true), s: tr('st_claim_s') });
    // The year to apply runs from each payment: the nearest open term is the
    // first payment's, not the last one's.
    if (r.nextDeadline) {
        const several = r.lastDeadline !== r.nextDeadline;
        const firstOpen = r.terms.find(t => !t.barred);
        stats.push({ l: tr('st_deadline'), v: numDay(r.nextDeadline), s: several ? tr('st_deadline_first', { date: numDay(firstOpen.paidOn), last: numDay(r.lastDeadline) }) : tr('st_deadline_s') });
    } else if (r.lastDeadline) {
        stats.push({ l: tr('st_deadline'), v: numDay(r.lastDeadline), s: tr('st_deadline_gone'), tone: 'bad' });
    }
    $('#di-stats').innerHTML = stats.map(s => `<div class="tc-stat ${s.tone || ''}"><div class="tc-stat-l">${esc(s.l)}</div><div class="tc-stat-v">${esc(s.v)}</div>${s.s ? `<div class="tc-stat-s">${esc(s.s)}</div>` : ''}</div>`).join('');

    const msgs = [];
    if (!r.executed) msgs.push({ t: tr('msg_unexecuted'), tone: 'warn' });
    else if (!r.paidInFull) msgs.push({ t: tr('msg_pending', { left: U.money(r.unpaid, cur, true), now: U.money(m.claimNow, cur, true), later: U.money(m.pending, cur, true) }), tone: 'warn' });
    if (r.clampedEnd || r.beyondData) msgs.push({ t: tr('msg_clamped', { last: U.monthYear(ds.k1), next: U.monthYear(ds.k1 + 1) }), tone: 'warn' });
    if (m.barred > 0) msgs.push({ t: tr('msg_barred', { sum: U.money(m.barred, cur, true) }), tone: 'bad' });
    if (r.negative) msgs.push({ t: tr('msg_negative', { sum: U.money(r.totalAll, cur, true) }), tone: 'warn' });
    if (r.ignored.some(i => i.why === 'before_start')) msgs.push({ t: tr('msg_before_start'), tone: 'warn' });
    if (r.overpaid > 0 || r.ignored.some(i => i.why === 'after_paid')) msgs.push({ t: tr('msg_overpaid'), tone: 'warn' });
    if (S.budget) msgs.push({ t: tr('msg_budget') });
    $('#di-msgs').innerHTML = msgs.map(m => `<p class="tc-msg ${m.tone || ''}">${esc(m.t)}</p>`).join('');

    renderTables(r, cur);
    renderStatement();
    render395(o);
    $('#di-result-more').hidden = false;
    T.result(JSON.stringify(state) + S.floorZero, {
        law: S.law, budget: S.budget ? 1 : 0, pays: o.payments.length, method: S.method, months: r.months.length,
        barred: m.barred > 0 ? 1 : 0, neg: r.negative ? 1 : 0, clamp: r.clampedEnd ? 1 : 0, full: r.paidInFull ? 1 : 0,
        exec: r.executed ? 1 : 0, st: statement && statement.ok ? 'ok' : (statement ? statement.why : ''),
    });
}

/**
 * Interest under art. 395 for the same sum and payments — computed only while
 * its drawer is open (a separate claim, not part of the indexation total).
 * Starts by default on the day after the decision: interest up to it is
 * usually in the decision itself (п. 48 Пленума ВС № 7).
 */
function render395(o) {
    last395 = null;
    if (!KEY_RATE || !$('#di-395').open) return;
    const start = L.parseDate(S.start);
    const from = S.i395.from || (start ? L.dateStr(L.fromDayNum(L.dayNum(start) + 1)) : '');
    if (!S.i395.from) $('#di-395-from').value = from;
    const r = L.interest395(KEY_RATE, { amount: o.amount, from, end: S.end, payments: o.payments, moratorium: S.i395.mor });
    const cur = ds.currency;
    const msgs = [];
    if (!r.ok) {
        $('#di-395-v').textContent = '—';
        $('#di-395-sub').textContent = r.order ? tr('i395_order') : '';
        $('#di-395-tbl').innerHTML = '';
        $('#di-395-msgs').innerHTML = '';
        return;
    }
    last395 = r;
    const num = d => U.numDate(L.parseDate(d));
    $('#di-395-v').textContent = U.money(r.total, cur, true);
    $('#di-395-sub').textContent = tr('i395_sub', { amount: U.money(o.amount, cur, true), from: num(r.from), to: num(r.end) });
    const dec = v => (Number.isInteger(v) ? 0 : Number.isInteger(v * 10) ? 1 : 2);
    const pct = v => U.num(v, dec(v)) + ' %';
    $('#di-395-tbl').innerHTML = `<thead><tr><th>${esc(tr('i395_col_period'))}</th><th class="num">${esc(tr('i395_col_base'))}</th><th class="num">${esc(tr('i395_col_rate'))}</th><th class="num">${esc(tr('i395_col_days'))}</th><th class="num">${esc(tr('i395_col_sum'))}</th></tr></thead><tbody>` +
        r.rows.map(x => `<tr><td class="di-nowrap">${esc(num(x.from))} – ${esc(num(x.to))}</td><td class="num" data-l="${esc(tr('i395_col_base'))}">${esc(U.money(x.base, cur, true))}</td><td class="num" data-l="${esc(tr('i395_col_rate'))}">${esc(x.mor ? tr('i395_mor_row') : pct(x.rate))}</td><td class="num" data-l="${esc(tr('i395_col_days'))}">${x.days}/${x.yearDays}</td><td class="num" data-l="${esc(tr('i395_col_sum'))}">${esc(U.money(x.sum, cur, true))}</td></tr>`).join('') +
        `<tr class="di-total"><td colspan="4">${esc(tr('i395_total'))}</td><td class="num">${esc(U.money(r.total, cur, true))}</td></tr></tbody>`;
    if (r.daysBefore2016) msgs.push({ t: tr('i395_before2016', { n: r.daysBefore2016 }), tone: 'warn' });
    if (r.daysMoratorium) msgs.push({ t: tr('i395_mor_days', { n: r.daysMoratorium }) });
    msgs.push({ t: tr('i395_rate_src', { rate: U.num(r.lastStep.rate, dec(+r.lastStep.rate)), date: num(r.lastStep.from) }) });
    $('#di-395-msgs').innerHTML = msgs.map(m => `<p class="tc-msg ${m.tone || ''}">${esc(m.t)}</p>`).join('');
    // The moratorium switch matters only when the period crosses 2022.
    $('#di-395-mor-wrap').hidden = !(r.from < '2022-10-01' && r.end >= '2022-04-01');
}

function setAnswer(value, sub, muted) {
    const v = $('#di-answer-v');
    v.textContent = value;
    v.classList.toggle('muted', !!muted);
    $('#di-answer-sub').textContent = sub || '';
    // The line under the fields: the sum, or what is still missing.
    const lv = $('#di-live-v');
    lv.textContent = muted ? (sub || value) : value;
    lv.classList.toggle('muted', !!muted);
}

/**
 * A month row names its days only when they are not the whole month:
 * «03.2023» for March, «10–31.01.2023 · 22/31» for the month of the decision.
 * Four columns instead of five fit a phone without a sideways scroll.
 */
function monthSpan(m) {
    const a = m.from, b = m.to;
    if (a.y !== b.y || a.m !== b.m) return U.numDate(a) + ' – ' + U.numDate(b);
    const mmYyyy = U.numDate(b).replace(/^\d{2}\D/, '');
    if (m.days === m.dim) return mmYyyy;
    return String(a.d).padStart(2, '0') + '–' + U.numDate(b) + ' · ' + m.days + '/' + m.dim;
}

function renderTables(r, cur) {
    $('#di-months').innerHTML = `<thead><tr><th>${esc(tr('col_period'))}</th><th class="num">${esc(tr('col_cpi'))}</th><th class="num">${esc(tr('col_coef'))}</th><th class="num">${esc(tr('col_cum'))}</th></tr></thead><tbody>` +
        r.months.map(m => `<tr${m.idx < 100 ? ' class="di-defl"' : ''}><td class="di-nowrap">${esc(monthSpan(m))}</td><td class="num">${U.num(m.idx, 2)}</td><td class="num">${U.num(m.coef, 6)}</td><td class="num">${U.num(m.cum, 6)}</td></tr>`).join('') + '</tbody>';
    // Four columns: the period goes under the part's name. With five the
    // total ran off the right edge of a 500 px stage — the one number people
    // came for was behind a sideways scroll.
    $('#di-pieces').innerHTML = `<thead><tr><th>${esc(tr('col_part'))}</th><th class="num">${esc(tr('col_sum'))}</th><th class="num">${esc(tr('col_cum'))}</th><th class="num">${esc(tr('col_indexation'))}</th></tr></thead><tbody>` +
        r.pieces.map(p => `<tr${p.timeBarred ? ' class="di-barred"' : ''}><td>${esc(pieceLabel(p))}${p.timeBarred ? ` <span class="di-tag">${esc(tr('barred_tag'))}</span>` : ''}<div class="di-span">${esc(U.numDate(p.from))} – ${esc(U.numDate(p.to))}</div></td><td class="num" data-l="${esc(tr('col_sum'))}">${esc(U.money(p.amount, cur, true))}</td><td class="num" data-l="${esc(tr('col_cum'))}">${U.num(p.K, 8)}</td><td class="num" data-l="${esc(tr('col_indexation'))}">${esc(U.money(p.kSum, cur, true))}</td></tr>`).join('') +
        `<tr class="di-total"><td colspan="3">${esc(tr('col_total'))}</td><td class="num">${esc(U.money(r.money.total, cur, true))}</td></tr></tbody>`;
    $('#di-formula').textContent = tr(r.method === 'parts' ? 'formula_parts' : 'formula_balance');
}

function pieceLabel(p) {
    if (p.kind === 'payment') return tr('part_paid', { date: U.numDate(p.paidOn) });
    if (p.kind === 'unpaid') return tr('part_unpaid');
    return tr('part_balance');
}

function calcText() {
    const r = last, cur = ds.currency;
    // The article is named only when someone has named the court: the sum does
    // not depend on it, and a calculation headed «ст. 208 ГПК» is wrong for an
    // arbitration case.
    const lawNamed = S.lawSet || S.law !== 'gpk';
    const lines = [
        lawNamed ? tr('copy_head', { law: tr('law_' + S.law) }) : tr('copy_head_plain'),
        tr('copy_sum', { amount: U.money(r.amount, cur, true), start: U.numDate(L.parseDate(r.start)), end: U.numDate(L.parseDate(r.end)) }),
        tr('copy_method_' + r.method),
        '',
        tr('copy_months'),
        ...r.months.map(m => `${U.numDate(m.from)}–${U.numDate(m.to)}: ${m.days}/${m.dim} ${tr('days_short')}, ${tr('cpi_word')} ${U.num(m.idx, 2)} % → ${U.num(m.coef, 6)}`),
        '',
        ...r.pieces.map(p => `${pieceLabel(p)}: ${U.money(p.amount, cur, true)} × (${U.num(p.K, 8)} − 1) = ${U.money(p.kSum, cur, true)}${p.timeBarred ? ' — ' + tr('barred_tag') : ''}`),
        '',
        tr('copy_total', { sum: U.money(r.money.total, cur, true) }),
        ...(r.executed && !r.paidInFull ? [tr('st_claim') + ': ' + U.money(r.money.claimNow, cur, true) + ' (' + tr('st_claim_s') + ').'] : []),
        ...(last395 ? [tr('i395_copy_head', { from: U.numDate(L.parseDate(last395.from)), to: U.numDate(L.parseDate(last395.end)), sum: U.money(last395.total, cur, true) }),
            ...last395.rows.map(x => `${U.numDate(L.parseDate(x.from))}–${U.numDate(L.parseDate(x.to))}: ${U.money(x.base, cur, true)} × ${x.mor ? 0 : x.rate} % × ${x.days}/${x.yearDays} = ${U.money(x.sum, cur, true)}`)] : []),
        tr('copy_source', { last: U.monthYear(ds.k1) }),
        location.href,
    ];
    return lines.join('\n');
}

/**
 * The application for the result on screen — or, when a court would not take
 * one, the reason and the next step in its place (no payment yet, the year
 * has passed, nothing to claim). The preview, the copied text and the Word
 * file are one list of blocks (statement-ru.js).
 */
function renderStatement() {
    const box = $('#di-statement'), none = $('#di-statement-none'), acts = $('#di-statement-acts');
    statement = null;
    if (last) {
        const lastPub = { y: C.ky(ds.k1), m: C.km(ds.k1), d: L.daysIn(C.ky(ds.k1), C.km(ds.k1)) };
        statement = ST.buildStatement({
            law: S.law, budget: S.budget, court: S.court, caseNo: S.caseNo, creditor: S.creditor, debtor: S.debtor,
            r: last, today, lastPub,
        }, U.T.stmt || {});
    }
    const ok = !!(statement && statement.ok);
    box.hidden = !ok;
    acts.hidden = !ok;
    none.hidden = ok || !statement;
    box.innerHTML = ok ? ST.toHtml(statement.blocks, esc) : '';
    if (statement && !ok) {
        none.textContent = tr('st_none_' + statement.why, { date: statement.deadline ? U.numDate(L.parseDate(statement.deadline)) : '' });
    }
}

/** The Word file: the application and, on its own page, the calculation. */
async function downloadStatement() {
    if (!statement || !statement.ok) return;
    const btn = $('#di-docx');
    if (btn.disabled) return;
    // The first click also fetches the zip library: on a slow line that takes
    // seconds, and a button that only greys out reads as a dead one.
    const label = btn.textContent;
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    btn.textContent = tr('docx_busy');
    try {
        if (!window.DocxWriter) throw new Error('docx-writer is not on the page');
        const blob = await window.DocxWriter.build({
            docTitle: tr('docx_name'), font: 'Times New Roman', size: 24, line: 360, lang: 'ru-RU',
            blocks: ST.toDocx(statement.blocks.concat(statement.appendix)),
        });
        const caseNo = S.caseNo.trim();
        const name = caseNo ? tr('docx_name_case', { case: caseNo }) : tr('docx_name');
        const safe = window.__outputName ? window.__outputName.sanitize(name) : tr('docx_name');
        const saved = await window.__downloadFile(blob, (safe || tr('docx_name')) + '.docx');
        if (saved !== false) T.action('docx', { law: S.law, filled: ['court', 'caseNo', 'creditor', 'debtor'].filter(k => S[k].trim()).length, parts: statement.rows.length });
    } catch (e) {
        window.__toast && window.__toast(tr('docx_failed'), 'error');
        U.diagnostic('docx_failed', { msg: String(e && e.message || e).slice(0, 120) });
    } finally {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        btn.textContent = label;
    }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
